import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { ErrorBoundary } from '@components/common/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import { Layout } from '@components/common/Layout';
import { ConsentPage } from './pages/ConsentPage';

// Lazy load non-critical pages (HomePage uses large MUI icons)
const HomePage = React.lazy(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));

// Lazy load Grant Management (less frequently used, includes React Query)
const GrantsLayout = React.lazy(() => import('./layouts/GrantsLayout').then(module => ({ default: module.GrantsLayout })));
const GrantListPage = React.lazy(() => import('./pages/GrantListPage').then(module => ({ default: module.GrantListPage })));
const GrantDetailsPage = React.lazy(() => import('./pages/GrantDetailsPage').then(module => ({ default: module.GrantDetailsPage })));

/**
 * Main application component with routing configuration.
 * Wrapped with ErrorBoundary and ToastProvider for error handling and notifications.
 *
 * Note: Authentication is handled by the backend via [Authorize] attribute.
 * Protected API endpoints return 401 if user is not authenticated.
 * Frontend components handle 401 responses and show appropriate error messages.
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Layout>
          <React.Suspense
            fallback={
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
              </Box>
            }
          >
            <Routes>
              {/* Home / Service Information */}
              <Route path="/" element={<HomePage />} />

              {/* OAuth2 Authorization Consent Flow */}
              <Route path="/authorize" element={<ConsentPage />} />

              {/* Grant Management with dedicated QueryClient (Backend-protected via [Authorize]) */}
              <Route path="/grants" element={<GrantsLayout />}>
                <Route index element={<GrantListPage />} />
                <Route path=":clientId" element={<GrantDetailsPage />} />
              </Route>

              {/* Fallback to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </React.Suspense>
        </Layout>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
